import { ThemeProvider } from '@mui/material/styles';
import { Suspense } from 'react';
import { Navigate, Routes as ReactRouterDOMRoutes, Route } from 'react-router-dom';

import RequireAuth from '@/components/routes/RequireAuth';

import LogIn from '@/pages/auth/LogIn';
import ResetPassword from '@/pages/auth/ResetPassword';
import Error404 from '@/pages/error/404';
import PatientHelp from '@/pages/help/PatientHelp';
import PatientOrders from '@/pages/patient/PatientOrders';
import PayByInvoice from '@/pages/payment/shopify/payByInvoice';
import PaymentConfirm from '@/pages/payment/shopify/paymentConfirm';
import PaymentSuccessAuth from '@/pages/payment/shopify/paymentSuccessAuth';
import PaymentSuccessUnauth from '@/pages/payment/shopify/paymentSuccessUnauth';
import ProductDetailsPage from '@/pages/products/portal-partner/ProductDetails';
import OrderMedicationPage from '@/pages/umeds/order-medication/OrderMedication';
import { uMeds } from '@montugroup/themes';

import SignOut from '../components/auth/SignOut';

import ForgotPassword from '@/components/auth/ForgotPassword';
import { HelpdeskLayout } from '@/components/layout/helpdesk-layout/HelpdeskLayout';
import { ScriptTokenStateProvider } from '@/context/escript/ScriptTokenContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Landing } from '@/pages/escript/landing/Landing';
import { OnboardingUnauth } from '@/pages/escript/onboarding/OnboardingUnauth';
import EScriptPatientOrders from '@/pages/escript/order-history/PatientOrders';
import { OrderMedication as EScriptOrderMedication } from '@/pages/escript/order-medication/OrderMedication';
import PaymentSuccessContainer from '@/pages/escript/order-success/PaymentSuccessContainer';
import HomePage from '@/pages/home/Home';
import { Prescription } from '@/pages/prescriptions/Prescription';
import { Prescriptions } from '@/pages/prescriptions/Prescriptions';
import CustomerProfile from '@/pages/profile/CustomerProfile';
import ClickAndCollect from '@/pages/shippingOptions/ClickAndCollect';
import ShippingOptions from '@/pages/shippingOptions/ShippingOptions';
import {
  ESCRIPT_ONBOARDING_ROUTE,
  ESCRIPT_ORDERS_ROUTE,
  ESCRIPT_PAYMENT_SUCCESS_ROUTE,
  ESCRIPT_PRODUCT_DETAIL_ROUTE,
  ESCRIPT_REFILL_PHARMACY_ROUTE,
  ESCRIPT_REFILL_ROUTE,
  FAQ_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  HEALTH_CHECK_ROUTE,
  HOME_ROUTE,
  LOGIN_ONBOARDKEY_ROUTE,
  LOGIN_PATIENT_ONBOARDKEY_ROUTE,
  LOGIN_ROUTE,
  LOGOUT_ROUTE,
  PATIENT_CLICK_AND_COLLECT,
  PATIENT_ORDERS_ROUTE,
  PATIENT_PAYMENT_SUCCESS_ROUTE,
  PATIENT_PRODUCT_DETAIL_ROUTE,
  PATIENT_REFILL_PHARMACY_ROUTE,
  PATIENT_REFILL_ROUTE,
  PAY_BY_INVOICE_ROUTE,
  PAYMENT_CONFIRM_ROUTE,
  PAYMENT_SUCCESS_ROUTE,
  POLICY_ROUTE,
  PRESCRIPTION_ROUTE,
  PRESCRIPTIONS_ROUTE,
  PROFILE_DETAILS_ROUTE,
  RESET_PASSWORD_LEGACY_ROUTE,
  RESET_PASSWORD_ROUTE,
  UMEDS_MARKETING,
} from '@/routes/route-paths';
import { CircularProgress, ToastContainer } from '@montugroup/design-system';
import { ExternalRedirect } from './ExternalRedirect';
import HealthCheck from './healthCheck';

function Routes() {
  const enableEscriptPrescriptionPages = useFeatureFlag('escript_enable_prescriptions_pages');

  return (
    <ThemeProvider theme={uMeds}>
      <ToastContainer // todo temp placement of toasts to consume the nearest theme provider. Top level theme provider needs to move as toasts have no need to be in routes
        // Allowing unmasking of toast text in session replays, if any toast text is found to be PII the implementation of this will need to be changed
        toastClassName="dd-privacy-allow"
        position="top-right"
        autoClose={7500}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnHover
      />
      <ReactRouterDOMRoutes>
        <Route path="/" Component={LogIn} />
        <Route path={LOGIN_ROUTE} Component={LogIn} />
        {/* TODO: Deprecated URL, remove after no longer used by CRM/users */}
        <Route path={LOGIN_PATIENT_ONBOARDKEY_ROUTE} Component={LogIn} />
        <Route path={LOGIN_ONBOARDKEY_ROUTE} Component={LogIn} />
        <Route path={FORGOT_PASSWORD_ROUTE} Component={ForgotPassword} />
        <Route path={RESET_PASSWORD_ROUTE} Component={ResetPassword} />
        <Route path={RESET_PASSWORD_LEGACY_ROUTE} Component={ResetPassword} />
        <Route path={RESET_PASSWORD_ROUTE} Component={ResetPassword} />
        <Route path={HEALTH_CHECK_ROUTE} Component={HealthCheck} />
        <Route path={POLICY_ROUTE} element={<ExternalRedirect to={UMEDS_MARKETING.PRIVACY_POLICY} />} />
        <Route path={PAY_BY_INVOICE_ROUTE} Component={PayByInvoice} />
        <Route path="/payment-confirm/cart" element={<Navigate to={{ pathname: PATIENT_REFILL_ROUTE }} replace />} />
        <Route path={PAYMENT_CONFIRM_ROUTE} Component={PaymentConfirm} />
        <Route path={PAYMENT_SUCCESS_ROUTE} Component={PaymentSuccessUnauth} />
        <Route element={<RequireAuth />}>
          <Route element={<HelpdeskLayout />}>
            <Route path={PATIENT_PRODUCT_DETAIL_ROUTE} element={<ProductDetailsPage />} />
            <Route path={PATIENT_ORDERS_ROUTE} element={<PatientOrders />} />
            <Route
              path={PATIENT_REFILL_PHARMACY_ROUTE}
              element={
                <Suspense fallback={<CircularProgress />}>
                  <ShippingOptions />
                </Suspense>
              }
            />
            <Route
              path={PATIENT_CLICK_AND_COLLECT}
              element={
                <Suspense fallback={<CircularProgress />}>
                  <ClickAndCollect />
                </Suspense>
              }
            />
            {enableEscriptPrescriptionPages && <Route path={PRESCRIPTIONS_ROUTE} element={<Prescriptions />} />}
            {enableEscriptPrescriptionPages && <Route path={PRESCRIPTION_ROUTE} element={<Prescription />} />}
            <Route path={PROFILE_DETAILS_ROUTE} element={<CustomerProfile />} />
            <Route
              path={ESCRIPT_REFILL_ROUTE}
              element={
                <ScriptTokenStateProvider>
                  <EScriptOrderMedication />
                </ScriptTokenStateProvider>
              }
            />
            <Route path={ESCRIPT_REFILL_PHARMACY_ROUTE} element={<ShippingOptions />} />
            <Route path={ESCRIPT_ORDERS_ROUTE} element={<EScriptPatientOrders />} />
            <Route path={ESCRIPT_PRODUCT_DETAIL_ROUTE} element={<ProductDetailsPage />} />
            <Route path={ESCRIPT_PAYMENT_SUCCESS_ROUTE} element={<PaymentSuccessContainer />} />
            <Route path={FAQ_ROUTE} element={<PatientHelp />} />
          </Route>

          {/* Isn't nested into HelpdeskLayout along with above routes, as we need to cater for positioning within this page */}
          <Route path={PATIENT_REFILL_ROUTE} element={<OrderMedicationPage />} />

          <Route path={HOME_ROUTE} element={<HomePage />} />
          <Route path={LOGOUT_ROUTE} element={<SignOut />} />
          <Route path="/paymentcheckout" element={<Navigate to={PATIENT_REFILL_ROUTE} replace />} />
          <Route path={PATIENT_PAYMENT_SUCCESS_ROUTE} element={<PaymentSuccessAuth />} />
        </Route>

        <Route
          path={ESCRIPT_ONBOARDING_ROUTE}
          element={
            <OnboardingUnauth>
              <Landing />
            </OnboardingUnauth>
          }
        />
        <Route path="*" Component={Error404} />
      </ReactRouterDOMRoutes>
    </ThemeProvider>
  );
}

export default Routes;
